import React, { useState, useEffect } from 'react';
import DashboardHeader from '../../layouts/dashboard_header';
import SideMenu from '../../layouts/dashboard_menu';
import DashboardFooter from '../../layouts/dashboard_footer';
import Canvas from '../../layouts/dahboard_canvas';
import { useBaseUrl, getJsonAuthHeaders } from '../../../utils/utils';

type QOTab = "overview" | "measures" | "benchmarks" | "peer";

interface QualityMeasure {
    name: string; my_rate: number; network_avg: number; target: number; trend: string;
}

interface PatientOutcome {
    patient: string; condition: string; outcome: string; period: string; status: string;
}

const QualityOutcomes: React.FC = () => {
    const [activeTab, setActiveTab] = useState<QOTab>('overview');
    const [measures, setMeasures] = useState<QualityMeasure[]>([]);
    const [outcomes, setOutcomes] = useState<PatientOutcome[]>([]);
    const [loading, setLoading] = useState(true);
    const baseUrl = useBaseUrl();

    useEffect(() => { document.title = "Uzima - Quality & Outcomes"; loadData(); }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            const headers = await getJsonAuthHeaders();
            const [mRes, oRes] = await Promise.allSettled([
                fetch(`${baseUrl}core/api/quality-measures/`, { headers }),
                fetch(`${baseUrl}core/api/patient-outcomes/`, { headers }),
            ]);
            if (mRes.status === 'fulfilled' && mRes.value.ok) { const d = await mRes.value.json(); if (d.status === 'success') setMeasures(d.data || []); }
            if (oRes.status === 'fulfilled' && oRes.value.ok) { const d = await oRes.value.json(); if (d.status === 'success') setOutcomes(d.data || []); }
        } catch (err) { console.error("Load error:", err); }
        finally { setLoading(false); }
    };

    const tabs: Array<{ key: QOTab; label: string; icon: string }> = [
        { key: "overview", label: "Quality Measures", icon: "bx bx-bar-chart-alt-2" },
        { key: "measures", label: "Patient Outcomes", icon: "bx bx-heart" },
        { key: "benchmarks", label: "Network Benchmarks", icon: "bx bx-line-chart" },
        { key: "peer", label: "Peer Review", icon: "bx bx-group" },
    ];

    return (
        <>
            <DashboardHeader />
            <SideMenu />
            <div className="main-content">
                <div className="page-content">
                    <div className="sc-wrap">
                        <div className="sc-header">
                            <div><h1>Quality & Outcomes</h1><p>Clinical quality measures, patient outcomes, and peer benchmarking</p></div>
                        </div>
                        <div className="sc-stats">
                            <div className="sc-stat"><div className="sc-stat-icon teal"><i className="bx bx-bar-chart-alt-2"></i></div><div className="sc-stat-value">{measures.length}</div><div className="sc-stat-label">Quality Measures</div></div>
                            <div className="sc-stat"><div className="sc-stat-icon blue"><i className="bx bx-heart"></i></div><div className="sc-stat-value">{outcomes.length}</div><div className="sc-stat-label">Patient Outcomes</div></div>
                            <div className="sc-stat"><div className="sc-stat-icon violet"><i className="bx bx-trending-up"></i></div><div className="sc-stat-value">{outcomes.filter(o => o.status === 'improving').length}</div><div className="sc-stat-label">Improving</div></div>
                            <div className="sc-stat"><div className="sc-stat-icon amber"><i className="bx bx-error-alt"></i></div><div className="sc-stat-value">{outcomes.filter(o => o.status === 'concern').length}</div><div className="sc-stat-label">Concerns</div></div>
                        </div>
                        <div className="sc-tabs">{tabs.map(t => (<button key={t.key} className={`sc-tab ${activeTab === t.key ? 'active' : ''}`} onClick={() => setActiveTab(t.key)}><i className={t.icon}></i> {t.label}</button>))}</div>
                        {loading ? <div className="sc-spinner"></div> : (
                            <>
                                {activeTab === 'overview' && (
                                    <div className="sc-panel"><div className="sc-panel-head"><h3>Quality Measures</h3></div>
                                        <div className="sc-panel-body">{measures.length === 0 ? (
                                            <div className="sc-empty"><i className="bx bx-bar-chart-alt-2"></i><h3>No Quality Measures</h3><p>Quality metrics will appear here once configured.</p></div>
                                        ) : (
                                            <div className="sc-list-stack">{measures.map((m, i) => (
                                                <div className="sc-list-item" key={i} style={{ flexDirection: 'column', alignItems: 'stretch' }}>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                        <strong style={{ fontSize: '13px' }}>{m.name}</strong>
                                                        <div style={{ display: 'flex', gap: '12px', fontSize: '12px', alignItems: 'center' }}>
                                                            <span style={{ color: 'var(--uzima-shell-muted)' }}>Network: {m.network_avg}%</span>
                                                            <span style={{ color: 'var(--uzima-shell-muted)' }}>Target: {m.target}%</span>
                                                            <span style={{ fontWeight: 600, color: 'var(--uzima-shell-accent)' }}>{m.my_rate}%</span>
                                                        </div>
                                                    </div>
                                                    <div style={{ background: 'var(--uzima-shell-border)', borderRadius: '6px', height: '6px', marginTop: '8px' }}>
                                                        <div style={{ background: m.my_rate >= m.target ? 'var(--uzima-shell-accent)' : '#f59e0b', borderRadius: '6px', height: '100%', width: `${m.my_rate}%`, transition: 'width 0.3s' }}></div>
                                                    </div>
                                                </div>
                                            ))}</div>
                                        )}</div>
                                    </div>
                                )}
                                {activeTab === 'measures' && (
                                    <div className="sc-panel"><div className="sc-panel-head"><h3>Patient Outcomes</h3></div>
                                        <div className="sc-panel-body">{outcomes.length === 0 ? (
                                            <div className="sc-empty"><i className="bx bx-heart"></i><h3>No Patient Outcomes</h3><p>Tracked patient outcomes will appear here.</p></div>
                                        ) : (
                                            <div className="sc-table-wrap"><table className="sc-table"><thead><tr><th>Patient</th><th>Condition</th><th>Outcome</th><th>Period</th><th>Trend</th></tr></thead>
                                                <tbody>{outcomes.map((o, i) => (<tr key={i}><td><strong>{o.patient}</strong></td><td><span className="sc-chip">{o.condition}</span></td><td>{o.outcome}</td><td>{o.period}</td>
                                                    <td><span className={`sc-badge-status ${o.status === 'improving' ? 'agreed' : o.status === 'stable' ? 'pending' : 'expired'}`}>{o.status}</span></td></tr>))}</tbody></table></div>
                                        )}</div>
                                    </div>
                                )}
                                {activeTab === 'benchmarks' && (
                                    <div className="sc-panel"><div className="sc-panel-body"><div className="sc-empty"><i className="bx bx-line-chart"></i><h3>Network Benchmarks</h3><p>See how your clinical quality compares across the entire Uzima provider network. Benchmarks are anonymized and aggregated by specialty.</p></div></div></div>
                                )}
                                {activeTab === 'peer' && (
                                    <div className="sc-panel"><div className="sc-panel-body"><div className="sc-empty"><i className="bx bx-group"></i><h3>Peer Review</h3><p>Participate in anonymous peer review with other clinicians on the Uzima network. Share cases and improve clinical practice collaboratively.</p>
                                        <button className="sc-btn sc-btn-primary" style={{ marginTop: '12px' }}><i className="bx bx-plus"></i> Submit a Case</button></div></div></div>
                                )}
                            </>
                        )}
                    </div>
                </div>
                <DashboardFooter />
            </div>
            <Canvas />
        </>
    );
};

export default QualityOutcomes;
